import { faChevronDown, faChevronRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  useAssignmentGetAutoApproveAnalytics,
  useAssignmentGetAutoApproveBlockedAssignments,
  useAssignmentGetAutoApproveUnApprovalAnalytics,
} from 'api/assignment/assignment';
import type { BlockedAssignmentDetail } from 'api/model/blockedAssignmentDetail';
import type { NotAutoApprovableReason } from 'api/model/notAutoApprovableReason';
import type { SingleBlockerReason } from 'api/model/singleBlockerReason';
import type { UnApprovalDetail } from 'api/model/unApprovalDetail';
import type { UnApprovalGroupBreakdown } from 'api/model/unApprovalGroupBreakdown';
import Modal from 'components/Modal';
import { useMemo, useState } from 'react';
import { generatePath, Link, Outlet } from 'react-router-dom';
import Routes from 'constants/Routes';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import styled from 'styled-components';
import themes from 'constants/Colors';
import themeSizes from 'constants/Sizes';

const PageContainer = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PageTitle = styled.h1`
  font-size: ${themeSizes.font.large};
  margin-bottom: 10px;
`;

const Card = styled.div<{ span?: number }>`
  padding: 15px;
  border: 1px solid ${themes.light.border.primary};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  grid-column: span ${({ span }) => span ?? 1};
`;

const CardTitle = styled.h2`
  font-size: ${themeSizes.font.medium};
  margin-bottom: 10px;
  padding-bottom: 5px;
  border-bottom: 1px solid ${themes.light.border.primary};
`;

const KPIGrid = styled.div<{ cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ cols }) => cols ?? 4}, 1fr);
  gap: 15px;
`;

const Tag = styled.span<{ color?: string }>`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  background-color: ${({ color }) => (color ? `${color}20` : '#eee')};
  color: ${({ color }) => color ?? '#333'};
  margin: 1px 2px;
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 12px 15px;
  background-color: #f8f9fa;
  border: 1px solid ${themes.light.border.primary};
  border-radius: 4px;
`;

const PresetButton = styled.button<{ active?: boolean }>`
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid ${({ active }) => (active ? '#17a2b8' : '#ccc')};
  background-color: ${({ active }) => (active ? '#17a2b820' : '#fff')};
  color: ${({ active }) => (active ? '#17a2b8' : '#333')};
  cursor: pointer;
  font-size: 13px;
`;

const DateInput = styled.input`
  padding: 5px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
`;

const StatBox = ({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub?: string;
  color: string;
}) => (
  <div
    style={{
      textAlign: 'center',
      padding: '15px',
      backgroundColor: `${color}15`,
      borderRadius: '8px',
      border: `2px solid ${color}`,
    }}
  >
    <div style={{ fontSize: '28px', fontWeight: 'bold', color }}>{value}</div>
    <div style={{ fontSize: '14px', fontWeight: '500', marginTop: '5px' }}>
      {label}
    </div>
    {sub && (
      <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
        {sub}
      </div>
    )}
  </div>
);

const COLORS = {
  green: '#28a745',
  red: '#dc3545',
  blue: '#17a2b8',
  orange: '#fd7e14',
  purple: '#6f42c1',
  gray: '#6c757d',
};

const ProgressBar = ({ value, max, color }: { value: number; max: number; color: string }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <div
        style={{
          flex: 1,
          height: '8px',
          backgroundColor: '#eee',
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            backgroundColor: color,
            borderRadius: '4px',
          }}
        />
      </div>
      <span style={{ fontSize: '11px', color: '#666', minWidth: '35px' }}>
        {pct.toFixed(0)}%
      </span>
    </div>
  );
};

const formatDateInput = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const startOfDay = (yyyymmdd: string): Date => {
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0);
};

const endOfDay = (yyyymmdd: string): Date => {
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999);
};

type DateRange = { fromYmd: string; toYmd: string };

const presetForDays = (days: number): DateRange => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (days - 1));
  return { fromYmd: formatDateInput(from), toYmd: formatDateInput(to) };
};

const DateRangeFilter = ({
  range,
  onChange,
}: {
  range: DateRange;
  onChange: (r: DateRange) => void;
}) => {
  const presets = [
    { label: '7 dagar', days: 7 },
    { label: '30 dagar', days: 30 },
    { label: '90 dagar', days: 90 },
  ];
  const activePreset = presets.find((p) => {
    const candidate = presetForDays(p.days);
    return candidate.fromYmd === range.fromYmd && candidate.toYmd === range.toYmd;
  });

  return (
    <FilterBar>
      <strong style={{ fontSize: '13px' }}>Period:</strong>
      {presets.map((p) => (
        <PresetButton
          key={p.days}
          active={activePreset?.days === p.days}
          onClick={() => onChange(presetForDays(p.days))}
        >
          {p.label}
        </PresetButton>
      ))}
      <span style={{ marginLeft: '12px', fontSize: '13px', color: '#666' }}>Från:</span>
      <DateInput
        type="date"
        value={range.fromYmd}
        max={range.toYmd}
        onChange={(e) => onChange({ ...range, fromYmd: e.target.value })}
      />
      <span style={{ fontSize: '13px', color: '#666' }}>Till:</span>
      <DateInput
        type="date"
        value={range.toYmd}
        min={range.fromYmd}
        onChange={(e) => onChange({ ...range, toYmd: e.target.value })}
      />
    </FilterBar>
  );
};

const UnApprovalDetailsTable = ({ details }: { details: UnApprovalDetail[] }) => {
  if (details.length === 0) {
    return (
      <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>
        Inga avgodkännanden i perioden
      </p>
    );
  }
  return (
    <div style={{ overflowY: 'auto', maxHeight: '500px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {['Uppdrag', 'Autogodkänt', 'Avgodkänt', 'Avgodkänt av', 'Fälttestare', 'Leverantör', 'Händelser', 'Utgifter', 'Vad som tillkom'].map((h) => (
              <th
                key={h}
                style={{
                  textAlign: 'left',
                  padding: '8px',
                  borderBottom: '2px solid #ddd',
                  position: 'sticky',
                  top: 0,
                  backgroundColor: '#fff',
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {details.map((d) => (
            <tr key={d.assignmentId}>
              <td style={{ padding: '6px 8px', fontSize: '13px' }}>
                <Link
                  to={generatePath(Routes.autoApproveAnalytics.case, { id: String(d.caseId) })}
                  style={{ color: COLORS.blue, textDecoration: 'none' }}
                >
                  {d.assignmentId}
                </Link>
              </td>
              <td style={{ padding: '6px 8px', fontSize: '12px' }}>
                {new Date(d.autoApprovedAt).toLocaleString('sv-SE')}
              </td>
              <td style={{ padding: '6px 8px', fontSize: '12px' }}>
                {new Date(d.unApprovedAt).toLocaleString('sv-SE')}
              </td>
              <td style={{ padding: '6px 8px', fontSize: '12px' }}>
                {d.unApprovedByUserName ?? '-'}
              </td>
              <td style={{ padding: '6px 8px', fontSize: '12px' }}>
                {d.fieldTesterName ?? '-'}
              </td>
              <td style={{ padding: '6px 8px', fontSize: '12px' }}>
                {d.supplierName ?? '-'}
              </td>
              <td style={{ padding: '6px 8px', fontSize: '12px' }}>
                {d.interveningEventCount}
              </td>
              <td style={{ padding: '6px 8px', fontSize: '12px' }}>
                {d.expenseChangesCount}
              </td>
              <td style={{ padding: '6px 8px' }}>
                {d.interveningEventTypes.map((t) => (
                  <Tag key={`e-${t}`} color={COLORS.orange}>{t}</Tag>
                ))}
                {d.expenseChangeTypes.map((t) => (
                  <Tag key={`x-${t}`} color={COLORS.purple}>{t}</Tag>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const UnApprovalGroupTable = ({
  rows,
  groupLabel,
}: {
  rows: UnApprovalGroupBreakdown[];
  groupLabel: string;
}) => {
  const filtered = rows.filter((r) => r.unApprovedCount > 0 || r.autoApprovedCount > 0).slice(0, 25);
  if (filtered.length === 0) {
    return (
      <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>
        Ingen data
      </p>
    );
  }
  return (
    <div style={{ overflowY: 'auto', maxHeight: '400px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {[groupLabel, 'Autogodkända', 'Avgodkända', 'Andel %', ''].map((h) => (
              <th
                key={h}
                style={{
                  textAlign: 'left',
                  padding: '8px',
                  borderBottom: '2px solid #ddd',
                  position: 'sticky',
                  top: 0,
                  backgroundColor: '#fff',
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.map((r, i) => (
            <tr key={`${r.groupId ?? 'na'}-${i}`}>
              <td style={{ padding: '6px 8px', fontSize: '13px' }}>{r.groupName}</td>
              <td style={{ padding: '6px 8px', fontSize: '13px' }}>{r.autoApprovedCount}</td>
              <td style={{ padding: '6px 8px', fontSize: '13px', color: r.unApprovedCount > 0 ? COLORS.red : undefined }}>
                {r.unApprovedCount}
              </td>
              <td style={{ padding: '6px 8px', fontSize: '13px' }}>
                {r.unApprovalRatePercent.toFixed(1)}%
              </td>
              <td style={{ padding: '6px 8px', fontSize: '13px', minWidth: '120px' }}>
                <ProgressBar
                  value={r.unApprovedCount}
                  max={r.autoApprovedCount}
                  color={r.unApprovalRatePercent > 20 ? COLORS.red : r.unApprovalRatePercent > 10 ? COLORS.orange : COLORS.green}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const categoryLabel = (category: string) =>
  category === 'GeneralDeviations'
    ? 'Generell'
    : category === 'ExecutionDeviations'
      ? 'Utförande'
      : 'Utgift';

const categoryColor = (category: string) =>
  category === 'GeneralDeviations'
    ? COLORS.red
    : category === 'ExecutionDeviations'
      ? COLORS.orange
      : COLORS.purple;

const STATUS_LABELS: Record<string, string> = {
  Approved: 'Godkänt',
  Complete: 'Slutfört',
  Canceled: 'Avbokat',
  CanceledNotFinished: 'Avbokat (ej klart)',
  Aborted: 'Avbrutet',
  AbortedNotFinished: 'Avbrutet (ej klart)',
  Discarded: 'Makulerat',
};

type BlockedSelection = { category: string; reason: string; singleBlocker: boolean };

const BlockedAssignmentsModal = ({
  selection,
  from,
  to,
  onClose,
}: {
  selection: BlockedSelection;
  from: Date;
  to: Date;
  onClose: () => void;
}) => {
  const { data, isLoading, isError } = useAssignmentGetAutoApproveBlockedAssignments({
    category: selection.category,
    reason: selection.reason,
    singleBlockerOnly: selection.singleBlocker,
    from,
    to,
  });

  const title = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '16px' }}>
      <Tag color={categoryColor(selection.category)}>{categoryLabel(selection.category)}</Tag>
      <span>{selection.reason}</span>
    </span>
  );

  return (
    <Modal onClose={onClose} title={title}>
      <div style={{ minWidth: '720px', maxWidth: '90vw' }}>
        {isError ? (
          <p style={{ color: COLORS.red, textAlign: 'center', padding: '20px' }}>
            Kunde inte hämta blockerade uppdrag. Försök igen.
          </p>
        ) : isLoading || !data ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          </div>
        ) : data.length === 0 ? (
          <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>
            Inga uppdrag i perioden
          </p>
        ) : (
          <>
            <p style={{ fontSize: '13px', color: '#555', marginTop: 0 }}>
              {data.length} blockerade uppdrag i vald period.
            </p>
            <div style={{ overflowY: 'auto', maxHeight: '60vh' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Uppdrag', 'Datum', 'Fälttestare', 'Leverantör', 'Status'].map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: 'left',
                          padding: '8px',
                          borderBottom: '2px solid #ddd',
                          position: 'sticky',
                          top: 0,
                          backgroundColor: '#fff',
                          fontSize: '12px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((d: BlockedAssignmentDetail) => (
                    <tr key={d.assignmentId}>
                      <td style={{ padding: '6px 8px', fontSize: '13px' }}>
                        <Link
                          to={generatePath(Routes.autoApproveAnalytics.case, { id: String(d.caseId) })}
                          style={{ color: COLORS.blue, textDecoration: 'none' }}
                        >
                          {d.assignmentId}
                        </Link>
                      </td>
                      <td style={{ padding: '6px 8px', fontSize: '12px' }}>
                        {d.bookedTo ? new Date(d.bookedTo).toLocaleDateString('sv-SE') : '-'}
                      </td>
                      <td style={{ padding: '6px 8px', fontSize: '12px' }}>
                        {d.fieldTesterName ?? '-'}
                      </td>
                      <td style={{ padding: '6px 8px', fontSize: '12px' }}>
                        {d.supplierName ?? '-'}
                      </td>
                      <td style={{ padding: '6px 8px', fontSize: '12px' }}>
                        {STATUS_LABELS[d.status] ?? d.status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

const AutoApproveAnalyticsPage = () => {
  const [range, setRange] = useState<DateRange>(() => presetForDays(30));
  const [unApprovalDetailsOpen, setUnApprovalDetailsOpen] = useState(false);
  const [selected, setSelected] = useState<BlockedSelection | null>(null);

  const queryParams = useMemo(
    () => ({
      from: startOfDay(range.fromYmd),
      to: endOfDay(range.toYmd),
    }),
    [range]
  );

  const { data, isLoading } = useAssignmentGetAutoApproveAnalytics(queryParams);
  const { data: unApprovalData, isLoading: unApprovalLoading } = useAssignmentGetAutoApproveUnApprovalAnalytics(queryParams);

  if (isLoading || !data) {
    return (
      <PageContainer>
        <PageTitle>Autogodkännande-analys</PageTitle>
        <DateRangeFilter range={range} onChange={setRange} />
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p style={{ marginTop: '10px' }}>Laddar analysdata...</p>
        </div>
      </PageContainer>
    );
  }

  const { summary, notAutoApprovableReasons, singleBlockerReasons } = data;

  return (
    <PageContainer>
      <PageTitle>Autogodkännande-analys</PageTitle>
      <DateRangeFilter range={range} onChange={setRange} />

      <p style={{ fontSize: '13px', color: '#555', margin: 0 }}>
        Samtliga siffror nedan exkluderar uppdrag där fälttestaren inte har autogodkännande aktiverat. Totalt utvärderade uppdrag (inkl. icke-aktiverade fälttestare):{' '}
        <strong>{summary.totalSnapshots}</strong>.
      </p>

      {/* Top KPI Summary */}
      <KPIGrid cols={2}>
        <StatBox
          label="Autogodkännandegrad"
          value={`${summary.autoApproveRatePercent}%`}
          sub={`${summary.totalAutoApproved} av ${summary.totalCompleted} avslutade uppdrag (slutförda + avbokade)`}
          color={
            summary.autoApproveRatePercent >= 50
              ? COLORS.green
              : summary.autoApproveRatePercent >= 25
                ? COLORS.orange
                : COLORS.red
          }
        />
        {unApprovalLoading || !unApprovalData ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <FontAwesomeIcon icon={faSpinner} spin />
          </div>
        ) : (
          <StatBox
            label="Avgodkännandegrad"
            value={`${unApprovalData.summary.unApprovalRatePercent}%`}
            sub={`${unApprovalData.summary.totalUnApproved} av ${unApprovalData.summary.totalAutoApproved} autogodkända ångrats`}
            color={
              unApprovalData.summary.unApprovalRatePercent > 20
                ? COLORS.red
                : unApprovalData.summary.unApprovalRatePercent > 10
                  ? COLORS.orange
                  : COLORS.green
            }
          />
        )}
      </KPIGrid>

      <Card span={2}>
        <CardTitle>
          Vanligaste anledningar till att uppdrag inte autogodkänts
        </CardTitle>
        {notAutoApprovableReasons.length === 0 ? (
          <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>
            Ingen data
          </p>
        ) : (
          <div style={{ overflowY: 'auto', maxHeight: '500px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Kategori', 'Avvikelse', 'Antal'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '8px',
                        borderBottom: '2px solid #ddd',
                        position: 'sticky',
                        top: 0,
                        backgroundColor: '#fff',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {notAutoApprovableReasons.map((r: NotAutoApprovableReason, i: number) => (
                  <tr
                    key={i}
                    role="button"
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    title="Visa uppdrag bakom denna avvikelse"
                    onClick={() => setSelected({ category: r.category, reason: r.reason, singleBlocker: false })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelected({ category: r.category, reason: r.reason, singleBlocker: false });
                      }
                    }}
                  >
                    <td style={{ padding: '6px 8px', fontSize: '12px' }}>
                      <Tag color={categoryColor(r.category)}>
                        {categoryLabel(r.category)}
                      </Tag>
                    </td>
                    <td style={{ padding: '6px 8px', fontSize: '12px', maxWidth: '350px', color: COLORS.blue }}>{r.reason}</td>
                    <td style={{ padding: '6px 8px', fontSize: '12px', fontWeight: 'bold' }}>{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card span={2}>
        <CardTitle>
          Single-blockers — om vi bortser från villkoret, hur många till skulle autogodkänts?
        </CardTitle>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
          Uppdrag där exakt ETT villkor stoppade autogodkännandet. &quot;Godkända&quot; är de som
          admin sedan godkände manuellt — om regeln slipades skulle just dessa konverterats
          till autogodkännanden direkt.
        </p>
        {singleBlockerReasons.length === 0 ? (
          <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>
            Ingen data
          </p>
        ) : (
          <div style={{ overflowY: 'auto', maxHeight: '500px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Kategori', 'Avvikelse', 'Totalt', 'Godkända', 'Ej godkända', 'Konverteringspotential'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '8px',
                        borderBottom: '2px solid #ddd',
                        position: 'sticky',
                        top: 0,
                        backgroundColor: '#fff',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {singleBlockerReasons.map((r: SingleBlockerReason, i: number) => (
                  <tr
                    key={i}
                    role="button"
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    title="Visa uppdrag bakom denna avvikelse"
                    onClick={() => setSelected({ category: r.category, reason: r.reason, singleBlocker: true })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelected({ category: r.category, reason: r.reason, singleBlocker: true });
                      }
                    }}
                  >
                    <td style={{ padding: '6px 8px', fontSize: '12px' }}>
                      <Tag color={categoryColor(r.category)}>
                        {categoryLabel(r.category)}
                      </Tag>
                    </td>
                    <td style={{ padding: '6px 8px', fontSize: '12px', maxWidth: '350px', color: COLORS.blue }}>{r.reason}</td>
                    <td style={{ padding: '6px 8px', fontSize: '12px', fontWeight: 'bold' }}>{r.count}</td>
                    <td style={{ padding: '6px 8px', fontSize: '12px', color: COLORS.green, fontWeight: 'bold' }}>{r.approvedCount}</td>
                    <td style={{ padding: '6px 8px', fontSize: '12px', color: COLORS.gray }}>{r.notApprovedCount}</td>
                    <td style={{ padding: '6px 8px', fontSize: '12px', minWidth: '120px' }}>
                      <ProgressBar value={r.approvedCount} max={r.count} color={COLORS.green} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card span={2}>
        <div
          role="button"
          tabIndex={0}
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '8px' }}
          onClick={() => setUnApprovalDetailsOpen(!unApprovalDetailsOpen)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setUnApprovalDetailsOpen(!unApprovalDetailsOpen);
            }
          }}
        >
          <FontAwesomeIcon icon={unApprovalDetailsOpen ? faChevronDown : faChevronRight} size="xs" />
          <CardTitle style={{ marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>
            Avgodkännanden
          </CardTitle>
        </div>
        {unApprovalDetailsOpen && (
          <div style={{ marginTop: '12px' }}>
            <p style={{ fontSize: '13px', color: '#555', marginBottom: '12px' }}>
              När en admin tar tillbaka ett autogodkänt uppdrag är det den tydligaste signalen
              att algoritmen gjorde fel bedömning.
            </p>
            {unApprovalLoading || !unApprovalData ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <FontAwesomeIcon icon={faSpinner} spin />
              </div>
            ) : (
              <>
                {unApprovalData.dailyTrend.length > 0 && (
                  <div style={{ marginBottom: '15px' }}>
                    <h3 style={{ fontSize: '14px', marginBottom: '8px' }}>Trend per dag</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={unApprovalData.dailyTrend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" fontSize={11} />
                        <YAxis fontSize={12} allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="autoApproved"
                          name="Autogodkända"
                          stroke={COLORS.blue}
                          strokeWidth={2}
                        />
                        <Line
                          type="monotone"
                          dataKey="unApproved"
                          name="Avgodkända"
                          stroke={COLORS.red}
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '15px' }}>
                  <div>
                    <h3 style={{ fontSize: '14px', marginBottom: '8px' }}>Per leverantör</h3>
                    <UnApprovalGroupTable rows={unApprovalData.supplierBreakdown} groupLabel="Leverantör" />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '14px', marginBottom: '8px' }}>Per fälttestare</h3>
                    <UnApprovalGroupTable rows={unApprovalData.fieldTesterBreakdown} groupLabel="Fälttestare" />
                  </div>
                </div>

                <h3 style={{ fontSize: '14px', marginBottom: '8px' }}>Detaljer per avgodkännande</h3>
                <UnApprovalDetailsTable details={unApprovalData.details} />
              </>
            )}
          </div>
        )}
      </Card>

      {selected && (
        <BlockedAssignmentsModal
          selection={selected}
          from={queryParams.from}
          to={queryParams.to}
          onClose={() => setSelected(null)}
        />
      )}

      {/* Ärendemodalen (Routes.autoApproveAnalytics.case) */}
      <Outlet />
    </PageContainer>
  );
};

export default AutoApproveAnalyticsPage;
